import { BarChart2, Layers, Settings, Target, Users, Zap, type LucideIcon } from "lucide-react";

export type ServiceSlug =
  | "operating-system"
  | "team-structure"
  | "strategy-execution"
  | "performance-discipline"
  | "project-leadership"
  | "systems-improvement"
  | "partner-alignment"
  | "structure-and-performance-reset";

export type ServicePath =
  | "/services/operating-system"
  | "/services/team-structure"
  | "/services/strategy-execution"
  | "/services/performance-discipline"
  | "/services/project-leadership"
  | "/services/systems-improvement"
  | "/services/partner-alignment"
  | "/services/structure-and-performance-reset";

export type Service = {
  slug: ServiceSlug;
  /** Canonical name. Use this everywhere the service is referred to by name. */
  name: string;
  to: ServicePath;
  icon: LucideIcon;
  /** One line summary used on cards. */
  blurb: string;
  /** Longer card detail. */
  details: string;
  /** Plain outcome statement. */
  outcome: string;
};

export const services: Service[] = [
  { slug: "partner-alignment", name: "Partner Alignment Review", to: "/services/partner-alignment", icon: Users, blurb: "Business partnership and shareholder alignment.", details: "Agree roles, reward, decisions, money, protection and exit, then brief your own lawyer and accountant.", outcome: "Agreed, in writing" },
  { slug: "structure-and-performance-reset", name: "Structure and Performance Reset", to: "/services/structure-and-performance-reset", icon: Layers, blurb: "Restructuring and performance management.", details: "The owner decides. Ajility plans and runs the process, with employment lawyer review before anything is communicated.", outcome: "A settled team" },
  {
    slug: "operating-system",
    name: "Operating System & Governance",
    to: "/services/operating-system",
    icon: Settings,
    blurb: "Operating rhythm, decision rights and reporting.",
    details: "The meetings, approvals and reports that keep the business moving without every decision returning to the owner.",
    outcome: "Business runs consistently",
  },
  {
    slug: "team-structure",
    name: "People, Culture & Accountability",
    to: "/services/team-structure",
    icon: Users,
    blurb: "Right people, right seats, clear ownership.",
    details:
      "Roles, decision rights and accountability, plus the values, feedback and recognition that hold the team together.",
    outcome: "Right people in right seats",
  },
  {
    slug: "strategy-execution",
    name: "Strategy to Execution",
    to: "/services/strategy-execution",
    icon: Target,
    blurb: "Strategy execution through focused 90-day sprints.",
    details: "Turn the plan into a few priorities, each with an owner, a date and weekly follow-through.",
    outcome: "Plans get delivered",
  },
  {
    slug: "performance-discipline",
    name: "Performance & Commercial Discipline",
    to: "/services/performance-discipline",
    icon: BarChart2,
    blurb: "KPI scorecards and leading indicators that protect margin.",
    details: "See the numbers that move before the month is over, with a scorecard leaders update and discuss.",
    outcome: "Real visibility",
  },
  {
    slug: "project-leadership",
    name: "Project & Initiative Leadership",
    to: "/services/project-leadership",
    icon: Layers,
    blurb: "Project governance for the initiatives that matter.",
    details: "Give one person clear ownership of the plan, risks, decisions and weekly follow-through.",
    outcome: "Momentum on key initiatives",
  },
  {
    slug: "systems-improvement",
    name: "Systems & Operational Improvement",
    to: "/services/systems-improvement",
    icon: Zap,
    blurb: "Process documentation that replaces heroics.",
    details: "Fix the steps that keep failing, write down the better way and train the people who use it.",
    outcome: "Scalable operations",
  },
];

export const serviceBySlug = (slug: ServiceSlug): Service =>
  services.find((s) => s.slug === slug)!;

export const serviceName = (slug: ServiceSlug): string => serviceBySlug(slug).name;
